import {
    AutocompleteSuggestions,
    SearchQuery
} from "../models/search_query";
import {MapSearchResult, RatingProfilesResult, SearchState, TagsSearchResult} from "../models/models";
import {SimpleCache} from "./simple_cache";
import {searchQueryFromSearchState, urlWithRatingProfileIdParam} from "./helpers";

const apiVersion = document.documentElement.dataset.apiVersion;
const apiVersionHeader = `application/vnd.ginto.v${apiVersion}+json`;
const baseUrl = new URL(window.location.toString());
baseUrl.hostname = baseUrl.hostname.replace('www.', '').replace (/^/,'api.');

function generateTagsSearchCacheKey(searchState: SearchState): string {
    const tags = [...searchState.searchQueryTags];
    let key = tags
        .sort((a, b) => {
                // First, compare by type
                if (a.type < b.type) return -1;
                if (a.type > b.type) return 1;

                // If type is the same, compare by key
                if (a.value < b.value) return -1;
                if (a.value > b.value) return 1;
                return 0;
            })
        .map(qt => qt.value)
        .join(';');

    const place = tags.find(qt => qt.type === "place");
    if (!place) {
        key += `;${searchState.mapInfo?.center?.lat}/${searchState.mapInfo?.center?.lng}`;
    }
    key += `;rp${searchState.ratingProfileId}`;

    return key;
}

function generateMapsSearchCacheKey(searchState: SearchState): string {
    const { mapInfo } = searchState;
    const northEast = mapInfo?.bounds?.getNorthEast();
    const southWest = mapInfo?.bounds?.getSouthWest();
    let key = generateTagsSearchCacheKey(searchState);

    key += `;c${mapInfo?.center?.lat}/${mapInfo?.center?.lng};ne${northEast?.lat}/${northEast?.lng};sw${southWest?.lat}/${southWest?.lng};z${mapInfo?.zoom}`;
    return key;
}

const tagsSearchCache = new SimpleCache<SearchState, Promise<TagsSearchResult>>(generateTagsSearchCacheKey, 10);
export async function tagsSearch(searchState: SearchState): Promise<TagsSearchResult> {
    const searchPromise = tagsSearchCache.get(searchState) ?? tagsSearchImpl(searchState);
    tagsSearchCache.set(searchState, searchPromise)

    return searchPromise;
}

async function tagsSearchImpl(searchState: SearchState): Promise<TagsSearchResult> {
    const apiUrl = urlWithRatingProfileIdParam(baseUrl.href, searchState.ratingProfileId);
    apiUrl.pathname = '/search/tags';

    const query: SearchQuery = searchQueryFromSearchState(searchState);
    const response = await fetch(apiUrl.href, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept-Language": document.documentElement.lang,
            "Accept": apiVersionHeader
        },
        body: JSON.stringify(query),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status} with body: ${await response.text()}`);
    const jsonResponse = await response.json();
    return jsonResponse as TagsSearchResult;
}

const mapsSearchCache = new SimpleCache<SearchState, Promise<MapSearchResult>>(generateMapsSearchCacheKey, 10);
export async function mapSearch(searchState: SearchState): Promise<MapSearchResult> {
    const searchPromise = mapsSearchCache.get(searchState) ?? mapSearchImpl(searchState);
    mapsSearchCache.set(searchState, searchPromise)

    return searchPromise;
}

async function mapSearchImpl(searchState: SearchState): Promise<MapSearchResult> {
    const apiUrl = urlWithRatingProfileIdParam(baseUrl.href, searchState.ratingProfileId);
    apiUrl.pathname = '/search/map';

    const query: SearchQuery = searchQueryFromSearchState(searchState);
    const response = await fetch(apiUrl.href, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept-Language": document.documentElement.lang,
            "Accept": apiVersionHeader
        },
        body: JSON.stringify(query),
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status} with body: ${await response.text()}`);
    const jsonResponse = await response.json();
    return jsonResponse as MapSearchResult;
}

let ratingProfilesCache: Promise<RatingProfilesResult[]>;
export function loadRatingProfiles() {
    const promise = ratingProfilesCache ?? loadRatingProfilesImpl();
    ratingProfilesCache = promise;

    return promise;
}

async function loadRatingProfilesImpl() {
    const apiUrl = new URL(baseUrl);
    apiUrl.pathname = '/rating_profiles';
    apiUrl.search = "";

    const response = await fetch(apiUrl.href, {
        method: "GET",
        headers: {
            "Content-Type": "application/json",
            "Accept-Language": document.documentElement.lang,
            "Accept": apiVersionHeader
        }
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status} with body: ${await response.text()}`);
    const jsonResponse = await response.json();
    return jsonResponse as RatingProfilesResult[];
}

let tagSuggestionsCache: Promise<AutocompleteSuggestions>;
export function loadTagSuggestions() {
    const suggestionsPromise = tagSuggestionsCache ?? tagSuggestionsImpl();
    tagSuggestionsCache = suggestionsPromise;

    return suggestionsPromise;
}

async function tagSuggestionsImpl() {
    const apiUrl = new URL(baseUrl);
    apiUrl.pathname = '/search/tag_suggestions';
    apiUrl.search = "";

    const response = await fetch(apiUrl.href, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Accept-Language": document.documentElement.lang,
            "Accept": apiVersionHeader
        },
    });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status} with body: ${await response.text()}`);
    const jsonResponse = await response.json();
    return jsonResponse as AutocompleteSuggestions;
}

const debouncedPlaceSuggestions = createDebouncedFetch<AutocompleteSuggestions>('/search/place_suggestions', 500, { method: "POST" });
const placeSuggestionsCache = new Map<string, Promise<AutocompleteSuggestions>>();
export function loadPlaceSuggestions(q: string, searchState: SearchState) {
    const criteria: SearchQuery = { q, ...searchQueryFromSearchState(searchState) }
    if (q.trim().length === 0) {
        return null;
    }

    const suggestionsPromise = placeSuggestionsCache.get(q) ?? debouncedPlaceSuggestions(criteria);
    placeSuggestionsCache.set(q, suggestionsPromise);

    return suggestionsPromise;
}


function createDebouncedFetch<T>(path: string, delay: number, options: RequestInit = {}): (data: any) => Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let abortController: AbortController;

    const apiUrl = new URL(baseUrl);
    apiUrl.pathname = path;
    apiUrl.search = "";

    return async (data: any): Promise<T> => {
        // Cancel previous timer and fetch
        if (abortController) {
            abortController.abort();
        }
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        abortController = new AbortController();

        return new Promise((resolve, reject) => {
            timeoutId = setTimeout(async () => {
                try {
                    const response = await fetch(apiUrl.href, {
                        ...options,
                        signal: abortController.signal,
                        body: JSON.stringify(data),
                        headers: {
                            'Content-Type': 'application/json',
                            "Accept-Language": document.documentElement.lang,
                            "Accept": apiVersionHeader,
                            ...(options.headers || {})
                        }
                    });

                    if (!response.ok) throw new Error(`HTTP error! status: ${response.status} with body: ${await response.text()}`);
                    const result = await response.json();
                    resolve(result as T);
                } catch (error) {
                    if ((error as Error).name === 'AbortError') {
                        // Silently ignore AbortError
                        return;
                    }
                    reject(error);
                }
            }, delay);
        });
    };
}
